import React, { useEffect, useReducer } from 'react';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useLocalisationContext } from '../localisation/context/LocalisationContext';
import { IdPrefixProvider } from '@jbhi-fi/components/src/IdPrefix';
import { drawBubbles } from '../bubble/drawBubbles';
import { HeaderLayout } from '@jbhi-fi/components/src/HeaderLayout';
import { useContentfulActiveNavs } from '@jbhi-fi/components/src/hooks/useContentfulActiveNavs/useContentfulActiveNavs';
import { NavTransformer } from '@jbhi-fi/components/src/Contentful/transformers/NavTransformer';
import { useHeaderUtilities } from '@jbhi-fi/components/src/hooks/useHeaderUtilities';
import resolveResponse from 'contentful-resolve-response';
import { ContentfulNav } from '@jbhi-fi/apis/src/contentful/types';
import { MyAccount } from '../myAccount/MyAccount';
import { Portal } from '../../../components/portal/Portal';
import { SearchPortal } from '../../../sections/search';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { breakpointSizePx } from '@jbhi-fi/lanyard-ui/themes';
import { useCouponContext } from '../../../contexts/CouponContextProvider';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getGapToDate } from '@jbhi-fi/utils/src/date/getGapToDate';
import { handleLoginCreate } from '../../../utils/signInOutUp/AuthenticationHelper';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { SnackbarProvider, useSnackbarContext } from '../snackbars/context/SnackbarContext';
import { MAIN_SNACKBAR_CLOSE_EVENT } from '@jbhi-fi/components/src/CarouselMainSnackbars/CarouselMainSnackbars';

export const HEADER_CONTAINER_ID = 'react-root-header';

const getInitialHeaderNavData = (): ContentfulNav | undefined => {
  try {
    const headerNavRawData = JSON.parse(decodeURIComponent(window?.Jbhifi?.headerNavDataString ?? '') || '{}');
    headerNavRawData.includes.asset = headerNavRawData.includes.asset || [];
    const headerNavData = resolveResponse(headerNavRawData);
    return headerNavData?.[0];
  } catch (err) {
    console.error('Error parsing header nav data', err);
    return undefined;
  }
};

const HeaderLocalised = () => {
  const { isCouponSurfacingEnabled, newCouponCount, coupons } = useCouponContext({ location: 'header' });
  const { isPerks, customerPreferences } = useCustomerContext();
  const { birthdayMonth } = useCustomerContext();
  const daysToBirthday = getGapToDate({ measure: 'days', month: birthdayMonth });
  const isCloseToBirthMonth = daysToBirthday > 0 && daysToBirthday <= 21;
  const isPerksSignupEnabled = useFeatureFlag('isHeaderPerksSignupEnabled');
  const isPerksBirthdayFormEnabled = useFeatureFlag('isHeaderPerksBirthdayFormEnabled');

  const { isLocalisationEnabled } = useLocalisationContext();
  const cart = useCartContext();

  const shouldShowBirthdayField =
    isPerksBirthdayFormEnabled &&
    isPerks &&
    !birthdayMonth &&
    customerPreferences?.shopify_perks_skipSetBirthday !== 'true';
  const shouldShowPerksSignup =
    !isPerks && isPerksSignupEnabled && customerPreferences?.perks_skipJoinPrompt !== 'true';

  const headerUtilities = useHeaderUtilities({
    totalCartQuantity: cart?.cart?.item_count ?? 0,
    newCouponCount: isCouponSurfacingEnabled ? newCouponCount : undefined,
    coupons: coupons,
    isPerks: isPerks,
    isCloseToBirthMonth: isCloseToBirthMonth,
    shouldShowPerksSignup: shouldShowPerksSignup,
    shouldShowBirthdayField: shouldShowBirthdayField,
    MyAccountSubMenuComponent: MyAccount,
    onLoginClick: () => {
      handleLoginCreate(window.location.href);
    },
  });
  const isMobile = useBreakpoint('md', 'down');
  const isDesktop = useBreakpoint('lg', 'up');
  const isTabletView = !isMobile && !isDesktop;

  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const isAnnouncementBarActive = document.getElementsByTagName('body')?.[0]?.classList.contains('has-announcement');

  const getHeaderHeight = () => {
    const headerHeight = document.getElementById('header-content-container')?.offsetHeight ?? 0;
    const announcementBarHeight =
      (document.querySelector('body.has-announcement #shopify-section-header .announcement') as HTMLElement)
        ?.offsetHeight ?? 0;
    const headerNavHeight =
      window.innerWidth <= breakpointSizePx.md ? 0 : document.getElementById('header-nav-container')?.offsetHeight ?? 0;
    const countdownHeight = document.getElementById('snackbar-countdown')?.offsetHeight ?? 0;

    const mainSnackbarHeight = document.getElementById('snackbar-main')?.offsetHeight ?? 0;
    const subSnackbarHeight = document.getElementById('snackbar-sub')?.offsetHeight ?? 0;
    return (
      headerHeight + announcementBarHeight + headerNavHeight + countdownHeight + mainSnackbarHeight + subSnackbarHeight
    );
  };

  const handleResize = () => {
    setTimeout(() => {
      const element = document.getElementById('page-body-container');
      if (element) {
        element.style.paddingTop = `${getHeaderHeight()}px`;
      }
      window.scrollTo(0, 0);
    }, 100); // Delay to ensure elements are rendered
  };

  useEffect(() => {
    const announcementBar = document.querySelector('#shopify-section-header .announcement');
    const handleClick = () => {
      handleResize();
      forceUpdate();
    };
    announcementBar?.addEventListener('click', () => {
      handleClick();
    });
    window.addEventListener(MAIN_SNACKBAR_CLOSE_EVENT, handleClick);

    return () => {
      announcementBar?.removeEventListener('click', handleResize);
      window.removeEventListener(MAIN_SNACKBAR_CLOSE_EVENT, handleResize);
    };
  }, []);

  const { headerNav } = useContentfulActiveNavs({});
  const initialHeaderNav = getInitialHeaderNavData();
  const { snackbarProps } = useSnackbarContext();

  const headerNavProps =
    headerNav || initialHeaderNav
      ? {
          ...NavTransformer.toHeaderNavigationProps(headerNav || initialHeaderNav!),
          isAnnouncementBarActive,
        }
      : undefined;

  useEffect(() => {
    handleResize();
    forceUpdate();
  }, [
    isMobile,
    isTabletView,
    isDesktop,
    isAnnouncementBarActive,
    snackbarProps?.countdown?.length,
    snackbarProps?.main?.length,
    snackbarProps?.sub?.length,
  ]);

  return (
    <>
      <HeaderLayout
        utilities={headerUtilities}
        headerNavigationProps={headerNavProps}
        isLocalisationEnabled={isLocalisationEnabled}
        snackbarProps={snackbarProps}
        logoSubtitle={window.Jbhifi.logoSubtitle}
      />
    </>
  );
};

export const HeaderPortal = () => {
  const container = window.document.getElementById(HEADER_CONTAINER_ID);

  useEffect(() => {
    drawBubbles();
  }, []);

  if (!container) return null;

  return (
    <>
      <Portal container={container}>
        <IdPrefixProvider prefix="header">
          <SnackbarProvider>
            <HeaderLocalised />
          </SnackbarProvider>
        </IdPrefixProvider>
      </Portal>
      <SearchPortal />
    </>
  );
};
